import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import { getFDHoldings } from "../../redux/fd/fdSlice";
import FDHoldingsTable from "../../components/HoldingsTable/FDHoldingsTable";
import HoldingsErrorDialog from "../../components/HoldingsTable/HoldingsErrorDialog";
import AppLoader from "../../components/AppLoader/AppLoader";
import {
  extractHoldingsList,
  extractHoldingsPagination,
  getHoldingsErrorMessage,
  HoldingsApiPayload,
  isHoldingsApiError,
} from "../../utils/holdingsApiUtils";

interface FD {
  fdId: number;
  id: number;
  noteficationId: number;
  schemeName: string;
  providerName: string;
  category: string;
  amount: number;
  fdStatus: number;
  transactionDate: string;
  investmentFrequency: string;
  interesetRate: number;
  grossInterest: number;
  transactions: [];
  endDate: number;
  investmentTenure: number;
  returnAmount: number;
  createdAt: string;
  type: string;
  daysLeft: number;
  userId: number;
  startDate: number;
  name: string;
  firstName: string;
  folioNo: string;
}

const FDHoldings: React.FC = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dispatch = useDispatch<any>();
  const [FD, setFD] = useState<FD[]>([]);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const { id } = useParams();
  const fetchInFlightRef = useRef(false);

  const fetchFD = useCallback(async () => {
    if (fetchInFlightRef.current) return;

    fetchInFlightRef.current = true;
    setLoader(true);

    try {
      const response = await dispatch(
        getFDHoldings({
          userId: Number(id),
          startDate: 0,
          endDate: 0,
          forRm: 1,
        })
      );

      if (getFDHoldings.rejected.match(response)) {
        setErrorMessage(getHoldingsErrorMessage(response.payload));
        setFD([]);
        return;
      }

      const payload = response.payload as HoldingsApiPayload;
      if (isHoldingsApiError(payload)) {
        setErrorMessage(getHoldingsErrorMessage(payload));
        setFD([]);
        return;
      }

      setFD(extractHoldingsList(payload) as FD[]);
      const pagination = extractHoldingsPagination(payload);
      setTotalPages(pagination.totalPages);
      setCurrentPage(pagination.pageIndex);
    } catch (error) {
      console.error("Error fetching FD data:", error);
      setErrorMessage("Failed to load fixed deposit holdings.");
      setFD([]);
    } finally {
      fetchInFlightRef.current = false;
      setLoader(false);
    }
  }, [dispatch, id]);

  useEffect(() => {
    fetchFD();
  }, [fetchFD]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setCurrentPage(0);
  };

  return (
    <>
      <AppLoader loading={loader} />
      <FDHoldingsTable
        data={FD}
        totalPages={totalPages}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        fetchData={fetchFD}
      />
      <HoldingsErrorDialog
        open={Boolean(errorMessage)}
        message={errorMessage}
        title="Unable to load fixed deposit holdings"
        onClose={() => setErrorMessage(null)}
      />
    </>
  );
};

export default FDHoldings;
